// Sprint V27 (Fr 27.02.2026) — Google Analytics 4 Custom Events.
//
// GA4 Base-Code (gtag.js, PageView, Consent-Mode v2) ist bereits in
// /public/index.html geladen — Consent-gated und mit Mess-ID G-S1LDLCSMF9.
// Bis der User Consent gibt, ist `window.gtag` zwar definiert, aber die
// gtag-Config wird erst nach `__f4s_grantAnalyticsConsent()` geladen und
// alle Events landen bis dahin nur im dataLayer (kein Netz-Traffic).
//
// Dieses Modul liefert die Custom-Events fuer den Sales/Retention-Funnel:
//   - trackSignUp         → GA4-Standard `sign_up`  (bei DOI-Confirm)
//   - trackGenerateLead   → GA4-Standard `generate_lead` (bei DOI-Confirm)
//   - trackPurchase       → GA4-Standard `purchase` (bei Ablefy-Success)
//   - trackUpgradeClick   → Custom-Event `upgrade_click`
//   - trackCheckoutStart  → GA4-Standard `begin_checkout`
//   - trackTestStart      → Custom `test_start` (QuickCheck Landing)
//   - trackTestComplete   → Custom `test_complete` (Fokustyp erkannt)
//   - trackDashboardView  → Custom `dashboard_view` (1x pro Session)
//   - trackAnliegenCreated → Custom `anliegen_created`
//   - trackAnliegenLimitReached → Custom `anliegen_limit_reached`
//   - setUserId           → gtag('config', ..., {user_id}) nach Login
//
// Alle Funktionen sind Fire-and-Forget: Analytics-Fehler brechen die UX nie.
// Idempotenz per window.__f4s_ga4_events (analog zu metaPixel.js).

const MEASUREMENT_ID = "G-S1LDLCSMF9";

/** Sprint V28 (Fr 27.02.2026) — GA4-Kontext aus dem Browser lesen und ans
 *  Backend uebergeben, damit Server-Side-Events (`sign_up`, `generate_lead`,
 *  `purchase` via Measurement Protocol) an den gleichen Browser-Session-
 *  Kontext andocken. `client_id` verknuepft Server-Events mit Browser-User,
 *  `session_id` mit der aktuellen Session.
 *
 *  Playbook: gtag('get', MID, 'client_id'|'session_id', callback).
 *  Fallback nach 500ms Timeout, damit blockierte gtag-Instances kein
 *  User-facing Freeze verursachen.
 */
export function getGaContext() {
  return new Promise((resolve) => {
    if (typeof window === "undefined" || typeof window.gtag !== "function") {
      return resolve({ client_id: null, session_id: null });
    }
    const out = { client_id: null, session_id: null };
    let remaining = 2;
    const done = () => {
      remaining -= 1;
      if (remaining === 0) resolve(out);
    };
    try {
      window.gtag("get", MEASUREMENT_ID, "client_id", (v) => { out.client_id = v || null; done(); });
      window.gtag("get", MEASUREMENT_ID, "session_id", (v) => { out.session_id = v || null; done(); });
    } catch (_e) {
      resolve(out);
    }
    // Safety-Timeout
    setTimeout(() => resolve(out), 500);
  });
}

function _once(key) {
  if (typeof window === "undefined") return false;
  window.__f4s_ga4_events = window.__f4s_ga4_events || {};
  if (window.__f4s_ga4_events[key]) return false;
  window.__f4s_ga4_events[key] = true;
  return true;
}

function _gtag(eventName, params = {}) {
  try {
    if (typeof window === "undefined") return;
    if (typeof window.gtag !== "function") return;
    window.gtag("event", eventName, params);
  } catch (_e) {
    // Fire-and-forget — Analytics-Fehler duerfen die UX nie stoeren.
  }
}

/** Nach Login: eindeutige User-ID an GA4 uebergeben fuer Cross-Device-
 *  Tracking. Verwendet die interne User-UUID (anonymisiert, kein PII). */
export function setUserId(userId) {
  try {
    if (!userId || typeof window === "undefined" || typeof window.gtag !== "function") return;
    window.gtag("config", MEASUREMENT_ID, { user_id: String(userId) });
  } catch (_e) { /* silent */ }
}

/** GA4-Standard `sign_up`. Sprint V28 (Fr 27.02.2026):
 *  Server-only-Policy (siehe utils/ga4_mp.py). Der Browser-Event ist deaktiviert,
 *  weil GA4 KEIN Cross-Source-Dedup fuer sign_up hat -> Server ist Single
 *  Source of Truth. Diese Funktion bleibt als API-Stub bestehen (No-Op),
 *  damit bestehende Call-Sites nicht brechen. */
export function trackSignUp(_opts = {}) {
  // No-op — sign_up wird ausschliesslich server-seitig via GA4 MP gefeuert
  // (utils/ga4_mp.py::send_ga4_sign_up), getriggert im doi_flow.py-Handoff.
}

/** GA4-Standard `generate_lead`. Sprint V28: Server-only (siehe trackSignUp). */
export function trackGenerateLead(_opts = {}) {
  // No-op — generate_lead server-only (utils/ga4_mp.py::send_ga4_generate_lead)
}

/** GA4-Standard `purchase`. Trigger: Ablefy-Success. */
export function trackPurchase({
  value,
  currency = "EUR",
  plan = "unknown",
  orderId = null,
} = {}) {
  if (typeof value !== "number" || value <= 0) return;
  const key = orderId ? `purchase:${orderId}` : `purchase:${plan}:${Date.now()}`;
  if (!_once(key)) return;
  _gtag("purchase", {
    transaction_id: orderId || `nogen_${Date.now()}`,
    value,
    currency,
    items: [{ item_id: plan, item_name: plan, price: value, quantity: 1 }],
  });
  // Sprint V33 (Sa 14.09.2026) — Funnel-Attribution: zusaetzlich `checkout_complete`
  // als Custom-Event, damit Conversion-Funnel in GA4-Reports als eigenes Ziel
  // (Purchase != Checkout-Complete) getrennt gemessen werden kann. Nutzt
  // denselben transaction_id, deshalb kein Doppel-Zaehl-Risiko.
  _gtag("checkout_complete", {
    transaction_id: orderId || `nogen_${Date.now()}`,
    value,
    currency,
    plan,
  });
  // Pending-Cart leeren — verhindert cancel_before_complete
  try {
    if (typeof window !== "undefined") window.localStorage.removeItem("f4s_checkout_pending");
  } catch (_e) { /* silent */ }
}

/** Sprint V33 (Sa 14.09.2026) — GA4-Standard `add_to_cart`.
 *  Trigger: Klick auf einen Plan-CTA auf /pricing (vor dem Ablefy-Redirect).
 *  Setzt auch localStorage-Marker fuer die spaetere cancel_before_complete-
 *  Detektion. */
export function trackAddToCart({ plan = "unknown", value = null, location = "pricing_card" } = {}) {
  const params = { currency: "EUR", plan, location };
  if (typeof value === "number" && value > 0) {
    params.value = value;
    params.items = [{ item_id: plan, item_name: plan, price: value, quantity: 1 }];
  }
  _gtag("add_to_cart", params);
  // Pending-Cart-Marker fuer cancel_before_complete
  try {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(
        "f4s_checkout_pending",
        JSON.stringify({ plan, value, location, ts: Date.now() }),
      );
    }
  } catch (_e) { /* silent */ }
}

/** Sprint V33 — Custom `cancel_before_complete`.
 *  Wird beim App-Mount gefeuert, wenn der User `add_to_cart` gefeuert hat
 *  (localStorage-Marker), aber ohne Purchase zurueckkommt. Threshold: 30s
 *  Wartezeit + kein Purchase-Signal (der raeumt den Marker sonst).
 */
export function detectCancelBeforeComplete() {
  try {
    if (typeof window === "undefined") return;
    const raw = window.localStorage.getItem("f4s_checkout_pending");
    if (!raw) return;
    const meta = JSON.parse(raw);
    if (!meta || !meta.ts) return;
    const elapsedMs = Date.now() - Number(meta.ts);
    // Frueher als 30s ist wahrscheinlich Reload/Tab-Switch -> ignorieren.
    // Aelter als 4h ist stale (User war laengst weg) -> Marker droppen ohne Event.
    if (elapsedMs < 30_000) return;
    if (elapsedMs > 4 * 60 * 60 * 1000) {
      window.localStorage.removeItem("f4s_checkout_pending");
      return;
    }
    // Nur einmal pro Session feuern
    if (!_once(`cancel_before_complete:${meta.plan || "unknown"}:${meta.ts}`)) return;
    _gtag("cancel_before_complete", {
      plan: meta.plan || "unknown",
      value: typeof meta.value === "number" ? meta.value : undefined,
      location: meta.location || null,
      elapsed_ms: elapsedMs,
    });
    window.localStorage.removeItem("f4s_checkout_pending");
  } catch (_e) { /* silent */ }
}


/** Custom `upgrade_click`. Trigger: Klick auf jeglichen Pro-CTA. */
export function trackUpgradeClick({ location = "unknown", plan = null } = {}) {
  _gtag("upgrade_click", { location, plan });
}

/** GA4-Standard `begin_checkout`. Trigger: Redirect zu Ablefy. */
export function trackCheckoutStart({ plan = "unknown", value = null } = {}) {
  const params = { currency: "EUR" };
  if (value) params.value = value;
  if (plan) params.items = [{ item_id: plan, item_name: plan, price: value, quantity: 1 }];
  _gtag("begin_checkout", params);
}

/** Custom `test_start`. Trigger: User startet QuickCheck auf Landing. */
export function trackTestStart({ variant = null } = {}) {
  const key = `test_start:${variant || "default"}`;
  if (!_once(key)) return;
  _gtag("test_start", { test_type: "quick_check_5", variant });
}

const FOKUSTYPEN = ["katze", "eule", "hund", "biene"];
const FOKUSTYP_KEY = "f4s_ga4_fokustyp";

function _setFokustypProperty(ft) {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("set", "user_properties", { fokustyp: ft });
    }
  } catch (_e) {
    // Fire-and-forget
  }
}

// Bei jedem Seitenaufruf erneut setzen, damit Folge-Seiten (z. B. nach DOI) die Eigenschaft tragen.
try {
  const stored = typeof window !== "undefined" ? window.localStorage.getItem(FOKUSTYP_KEY) : null;
  if (FOKUSTYPEN.includes(stored)) _setFokustypProperty(stored);
} catch (_e) { /* noop */ }

/** Fokustyp steht fest: setzt GA4-Nutzer-Eigenschaft `fokustyp` (fuer alle
 *  Folge-Events) und feuert das eigene Event `fokustyp_assigned`. */
export function trackFokustypAssigned({ fokustyp = null, questionSet = null } = {}) {
  const ft = String(fokustyp || "").trim().toLowerCase();
  if (!FOKUSTYPEN.includes(ft)) return;
  _setFokustypProperty(ft);
  try { window.localStorage.setItem(FOKUSTYP_KEY, ft); } catch (_e) { /* noop */ }
  if (!_once(`fokustyp_assigned:${ft}`)) return;
  _gtag("fokustyp_assigned", { fokustyp: ft, question_set: questionSet });
}

/** Custom `test_complete`. Trigger: Fokustyp erkannt am Ende des Tests. */
export function trackTestComplete({ fokustyp = null, variant = null } = {}) {
  const key = `test_complete:${variant || "default"}`;
  if (!_once(key)) return;
  _gtag("test_complete", {
    test_type: "quick_check_5",
    fokustyp,
    variant,
  });
}

/** Custom `dashboard_view`. Nur einmal pro Session (Session = Tab-Leben). */
export function trackDashboardView({ tier = "free", hasFokustyp = false } = {}) {
  if (!_once("dashboard_view:session")) return;
  _gtag("dashboard_view", { tier, has_fokustyp: !!hasFokustyp });
}

/** Custom `anliegen_created`. */
export function trackAnliegenCreated({ tier = "free", fokustyp = null } = {}) {
  _gtag("anliegen_created", { tier, fokustyp });
}

/** Custom `anliegen_limit_reached`. Free-User hit 3/3 heute. */
export function trackAnliegenLimitReached({ used = 3, limit = 3 } = {}) {
  if (!_once("anliegen_limit_reached:session")) return;
  _gtag("anliegen_limit_reached", { used, limit });
}

// ---------------------------------------------------------------------------
// Sprint V96.3 (Feb 2026) — Video-Bridge GA4-Events.
// Client-side Fire-and-Forget (Server-Side ist ueber Meta CAPI abgedeckt).
// Event-Namen nach GA4-Recommended-Convention (`video_start`, `video_complete`)
// wo moeglich, sonst eigene `video_bridge_*`-Custom-Events.
// ---------------------------------------------------------------------------

/** Custom `video_bridge_shown`. Trigger: Result + Video-Bridge sichtbar. */
export function trackVideoBridgeShown({ fokustyp = null, hasVideo = false } = {}) {
  const key = `video_bridge_shown:${fokustyp || "unknown"}`;
  if (!_once(key)) return;
  _gtag("video_bridge_shown", { fokustyp, has_video: !!hasVideo });
}

/** GA4-Recommended `video_start`. Trigger: Vimeo-play-Event. */
export function trackVideoBridgePlay({ fokustyp = null, videoId = null } = {}) {
  const key = `video_bridge_play:${fokustyp || "unknown"}`;
  if (!_once(key)) return;
  _gtag("video_start", {
    video_provider: "vimeo",
    video_title: `fokustyp_bridge_${fokustyp || "unknown"}`,
    video_id: videoId,
    fokustyp,
  });
}

/** GA4-Recommended `video_complete`. Trigger: Vimeo-ended-Event. */
export function trackVideoBridgeComplete({ fokustyp = null, videoId = null } = {}) {
  const key = `video_bridge_complete:${fokustyp || "unknown"}`;
  if (!_once(key)) return;
  _gtag("video_complete", {
    video_provider: "vimeo",
    video_title: `fokustyp_bridge_${fokustyp || "unknown"}`,
    video_id: videoId,
    fokustyp,
  });
}

/**
 * GA4-Recommended `video_progress`. Trigger: Vimeo-timeupdate-Event bei
 * Erreichen von 25 % / 50 % / 75 %. Pro Session + Fokustyp + Milestone
 * nur einmal (via `_once`).
 */
export function trackVideoBridgeProgress({ fokustyp = null, videoId = null, percent = 25 } = {}) {
  const pct = [25, 50, 75].includes(percent) ? percent : 25;
  const key = `video_bridge_progress_${pct}:${fokustyp || "unknown"}`;
  if (!_once(key)) return;
  _gtag("video_progress", {
    video_provider: "vimeo",
    video_title: `fokustyp_bridge_${fokustyp || "unknown"}`,
    video_id: videoId,
    video_percent: pct,
    fokustyp,
  });
}



/** Custom `video_bridge_cta_click`. Trigger: CTA unter dem Video geklickt. */
export function trackVideoBridgeCtaClick({ fokustyp = null } = {}) {
  _gtag("video_bridge_cta_click", { fokustyp });
}

/**
 * Sprint V103.d (06.10.2026) — `results_cta_click`.
 *
 * Trigger: Klick auf den Opt-in-Knopf der Ergebnis-Seite — unabhaengig
 * davon, ob der Besucher die Video-Bruecke sieht (V99-Variante A) oder
 * nicht (Variante B). Platzierung: in der gemeinsamen Verarbeitungs-
 * stelle `LandingPage.handleQuickTestMagicLink`, nicht in den Varianten
 * selbst — damit die Luecke nicht bei jeder neuen Variante wieder
 * aufreisst.
 *
 * Verhaeltnis zu `video_bridge_cta_click`:
 *   - Variante A (mit Video): Der Video-Bruecke-Button feuert weiter
 *     `video_bridge_cta_click`. Der Follow-Up-Opt-in-Button auf der
 *     Ergebnis-Seite feuert zusaetzlich `results_cta_click`.
 *   - Variante B (ohne Video): Nur `results_cta_click`.
 *   - GA4-Trichter zaehlt pro Nutzer/Schritt nur einmal — keine
 *     Doppelzaehlung in Variante A.
 *
 * Zweck im Trichter: Schritt 4 (Opt-in geklickt) wird zu einer ODER-
 * Verknuepfung aus `video_bridge_cta_click` ODER `results_cta_click` —
 * damit erfasst der Schritt zum ersten Mal beide Pfade vollstaendig.
 */
export function trackResultsCtaClick({
  fokustyp = null,
  questionSet = null,
  hookVariant = null,
  source = null,
} = {}) {
  _gtag("results_cta_click", {
    fokustyp,
    question_set: questionSet,
    hook_variant: hookVariant,
    source,
  });
}


/** Challenge v2: `challenge_<step>` (link_opened, join_completed, day_started, day_completed,
 *  wrapup_opened, checkout_opened). Server-Events (pro_trial_started etc.) kommen aus dem Webhook. */
export function trackChallenge(step, params = {}) {
  _gtag(`challenge_${step}`, { challenge: "v2", ...params });
}

/** Landingpage „4-Fokustypen-Formel“: Klick auf einen Checkout-Button. */
export function trackFormelDownsell(pageVariant, offerPhase) {
  _gtag("formel_downsell_click", { page_variant: pageVariant, offer_phase: offerPhase, transport_type: "beacon" });
}

export function trackFormelCta(position, pageVariant, offerPhase) {
  let utm = {};
  try {
    const q = new URLSearchParams(window.location.search);
    utm = Object.fromEntries(["utm_source", "utm_medium", "utm_campaign", "utm_content"].map((k) => [k, q.get(k) || undefined]));
  } catch (_e) { /* noop */ }
  _gtag("formel_cta_click", { position, page_variant: pageVariant, offer_phase: offerPhase, transport_type: "beacon", ...utm });
}
